import React, { useMemo, useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";
import { diff } from "json8-patch";
import { FormikHelpers } from "formik";

import {
  SubSection,
  DataSection,
} from "../../../../components/data-components/sections/DataSections";
import {
  SectionMenuItemsModel,
  SectionDynamicMenuItemsPages,
} from "../../../sewer-objects/section-details/SectionMenuItems";
import {
  EditInspection,
  EditInspectionFromAssignedProject,
} from "../../../../services/authorization/Permissions";
import {
  DisplayInspectionName,
  DisplayInspectionRelevance,
  DisplayLinkedProject,
} from "../components/DataComponents";
import { EditDynamicProperties } from "../../../../components/data-components/EditDynamicProperties";
import PageLayout from "../../../_layout/PageLayout";
import Form from "../../../../components/form/Form";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import { CurrentSectionStorageModel } from "../../../../services/sewer-object/CurrentSectionStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { CurrentInspectionStorageModel } from "../../../../services/inspection/CurrentInspectionStorage";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { warningLeavePageService } from "../../../../services/page/WarningLeavePageService";
import { navigatePath } from "../../../../helpers/StringHelpers";
import useRouteService from "../../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import { EditInspectionRelevance } from "../../components/DataComponents";
import WarningBeforeAction from "../../../../components/data-components/WarningBeforeAction";
import useModal from "../../../../components/dialog/useModal";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import { FieldsFromBM } from "../../../../api-models/response/BluemetricFields";

export default observer(() => {
  const urlParamsService = useUrlParams();
  const history = useHistory();
  const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();
  const { isVisible, toggleModal } = useModal();

  const [formikValues, setFormikValues] = useState<FieldsFromBM>({});
  const [formikHelpers, setFormikHelpers] = useState<FormikHelpers<FieldsFromBM> | undefined>(
    undefined
  );

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: urlParamsService.getSectionId()!,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const releaseCurrentInspection = useCallback(async () => {
    await currentInspectionStorage.releaseInspection();
  }, [currentInspectionStorage]);

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      inspectionId: currentInspectionId,
      dynamicMenuItemPage: SectionDynamicMenuItemsPages.EditInspection,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentInspectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.currentSectionId,
    currentSectionStorage.parentEntityFromPath.id,
  ]);

  const getInspectionIsLockedWarning = useCallback(() => {
    if (
      currentInspectionStorage.userWithLockOnInspection &&
      !currentInspectionStorage.currentUserHasLockOnInspection
    )
      return {
        key: "EditInspection.locked-inspection",
        context: { userName: currentInspectionStorage.userWithLockOnInspection.name },
      };
    else return undefined;
  }, [
    currentInspectionStorage.userWithLockOnInspection,
    currentInspectionStorage.currentUserHasLockOnInspection,
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [currentInspectionStorage.currentInspection, currentSectionStorage.currentSection]
  );

  const canUserEditInspection = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        EditInspection,
        EditInspectionFromAssignedProject,
      ]),
    []
  );
  const canSubmitEdit = useMemo(
    () => !currentInspectionStorage.inspectionIsLockedBySomeoneElse && canUserEditInspection,
    [canUserEditInspection, currentInspectionStorage.inspectionIsLockedBySomeoneElse]
  );

  const getInitialValues = useCallback(
    () => ({
      ...currentInspectionStorage.currentInspection.getDynamicPropertiesKeyValueForEdit(),
      isRelevant: currentInspectionStorage.currentInspection.isRelevant.toString(),
    }),
    [currentInspectionStorage.currentInspection]
  );

  const areThereMultipleOutdatedInspectionsWithSameGatheringDate = () => {
    const inspectionsWithSameGatheringDate = currentInspectionStorage.currentInspection
      .gatheringDate
      ? currentSectionStorage.getOtherOutdatedInspectionsWithSameGatheringDate(
          currentInspectionStorage.currentInspection
        )
      : [];

    return inspectionsWithSameGatheringDate.length > 0;
  };

  const leavePage = useCallback(async () => {
    warningLeavePageService.setIsDirty(false);
    await releaseCurrentInspection();
    currentInspectionStorage.setJsonPatch([]);
    history.push(`${navigatePath(history.location.pathname, -1)}`);
  }, [currentInspectionStorage, history, releaseCurrentInspection]);

  const getPatchJson = useCallback(
    (values: FieldsFromBM) => {
      const cleanedFormatValues =
        currentInspectionStorage.currentInspection.getDynamicPropertiesWithDecimalsAndDateFormatted(
          values
        );
      return diff(getInitialValues(), cleanedFormatValues);
    },
    [currentInspectionStorage.currentInspection, getInitialValues]
  );

  const editInspection = useCallback(async () => {
    if (
      currentInspectionStorage.isRelevantOperation &&
      currentInspectionStorage.isRelevantOperation.value === "false"
    ) {
      try {
        await currentInspectionStorage.validateIfInspectionCanBeMarkedAsOutdated();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withModalTitleKey("ViewInspection.toggle-relevance.error.title");
        handleErrorInModal(applicationError);
        return false;
      }
    }

    await currentInspectionStorage.editInspectionDetails();
    return true;
  }, [currentInspectionStorage, handleErrorInModal]);

  const confirmMarkOthersRelevantAction = useCallback(async () => {
    try {
      await currentInspectionStorage.markOtherInspectionsAsRelevant();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withModalTitleKey("ViewInspection.toggle-relevance.error.title");
      handleErrorInModal(applicationError);
    }
  }, [currentInspectionStorage, handleErrorInModal]);

  const handleSubmit = async (values: FieldsFromBM, helpers: FormikHelpers<FieldsFromBM>) => {
    setFormikValues(values);
    setFormikHelpers(helpers);

    currentInspectionStorage.setJsonPatch(getPatchJson(values));

    if (
      currentInspectionStorage.isRelevantOperation &&
      currentInspectionStorage.isRelevantOperation.value === "true" &&
      areThereMultipleOutdatedInspectionsWithSameGatheringDate()
    ) {
      toggleModal();
      return;
    }

    try {
      const shouldLeavePage = await editInspection();
      snackbarNotificationsService.showSuccess("general.inspection.edit.success.snackbar");

      if (shouldLeavePage) {
        return leavePage();
      }
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(values, helpers);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function lockCurrentInspection() {
      await currentInspectionStorage.lockInspection();
    }

    lockCurrentInspection();

    return function cleanup() {
      releaseCurrentInspection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
        {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName(),
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname,
  ]);

  const markAllAsRelevantAction = useCallback(async () => {
    try {
      await editInspection();
      await confirmMarkOthersRelevantAction();
      snackbarNotificationsService.showSuccess("general.inspection.edit.success.snackbar");
      return leavePage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(formikValues, formikHelpers!);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  }, [
    confirmMarkOthersRelevantAction,
    editInspection,
    formikHelpers,
    formikValues,
    handleErrorInSnackbar,
    leavePage,
  ]);

  const markSelectionAsRelevantAction = useCallback(async () => {
    try {
      await editInspection();
      snackbarNotificationsService.showSuccess("general.inspection.edit.success.snackbar");

      return leavePage();
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withForm(formikValues, formikHelpers!);
      applicationError.withErrorMessage("default-error.edit");
      handleErrorInSnackbar(applicationError);
    }
  }, [editInspection, formikHelpers, formikValues, handleErrorInSnackbar, leavePage]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[EditInspection, EditInspectionFromAssignedProject]}
      titleKey="EditInspection.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
      }}
      id="edit-section"
      menuItems={menuItems}
      isLoading={currentSectionStorage.isLoading || currentInspectionStorage.isLoading}
    >
      <Form
        formikOptions={{
          initialValues: getInitialValues(),
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
          enableReinitialize: true,
        }}
        warningKey={getInspectionIsLockedWarning()?.key}
        warningContext={getInspectionIsLockedWarning()?.context}
        canSubmit={canSubmitEdit}
        onCancel={leavePage}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.inspections.details">
                <SubSection>
                  <DisplayInspectionName name={`Inspection ${getCurrentInspectionName()}`} />
                  <DisplayLinkedProject
                    project={currentInspectionStorage.currentInspection.projectName}
                  />
                  {currentSectionStorage.currentSection.inspectionsWithDates.length === 1 ? (
                    <DisplayInspectionRelevance
                      isRelevant={currentInspectionStorage.currentInspection.isRelevant}
                    />
                  ) : (
                    <EditInspectionRelevance
                      isRelevant={currentInspectionStorage.currentInspection.isRelevant}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </SubSection>
                <SubSection>
                  <DisplayDateCreated date={currentInspectionStorage.currentInspection.created} />
                  <DisplayCreatedBy
                    createdBy={currentInspectionStorage.currentInspection.createdBy.name}
                  />
                </SubSection>
                <SubSection />
              </DataSection>
              <EditDynamicProperties
                labelKey="general.characteristics"
                properties={currentInspectionStorage.currentInspection.getDynamicPropertiesForEdit()}
                values={values}
                setFieldValue={setFieldValue}
              />
            </>
          );
        }}
      </Form>
      <WarningBeforeAction
        isVisible={isVisible}
        titleKey="general.inspection.relevance.mark-relevant.modal.title"
        subtitleKey={"general.inspection.mark-relevant.modal.subtitle"}
        toggleModal={toggleModal}
        buttonOk={{
          id: "mark-all-button",
          textKey: "general.inspection.mark-relevant.modal.mark-all-button",
          action: markAllAsRelevantAction,
        }}
        additionalButton={{
          id: "mark-selection-button",
          textKey: "general.inspection.mark-relevant.modal.mark-selection-button",
          action: markSelectionAsRelevantAction,
        }}
      />
    </PageLayout>
  );
});
